import React, { useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { Button, Checkbox, Col, DatePicker, Form, Input, InputNumber, Row, Select, Spin } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Title from 'antd/es/typography/Title';
import { APP_LOCAL_DATE_FORMAT, APP_LOCAL_DATE_FORMAT_PAYLOAD } from 'app/config/constants';
import { checkIntKrQualDraftYear, getKrQualCheckInDraftYear, reset } from 'app/entities/checkIn/checkIn-draft.reducer';
import CommentComponent from 'app/entities/comment/comment';
import ObjectiveUpdate from 'app/entities/objective/form/objective-update';
import { EvaluationTypes } from 'app/shared/model/enum/evaluation-type.enum';
import { KrTypes } from 'app/shared/model/enum/kr.enum';
import { StatusType } from 'app/shared/model/enum/status.enum';
import { formatNumber, parseNumber } from 'app/shared/util/help';
import { displayPercent, percent } from 'app/shared/util/percent';
import { filterOption } from 'app/shared/util/select-filter';
import dayjs from 'dayjs';
import KRUpdate from '../../kr-update';
import ReportQualitativeResultView from '../view/reportQualitativeResult';
import ReportQuantitativeResultView from '../view/reportQuantitativeResult';

type IReportQualitativeResult = {
  handleClose: (isReload?: boolean) => void;
  krCheckIn?: any;
  handleNextData: () => void;
  setUpdateSuccess: (isReload?: boolean) => void;
};

export const ReportQualitativeResult = ({ handleClose, krCheckIn, handleNextData, setUpdateSuccess }: IReportQualitativeResult) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const filters = useAppSelector(state => state.filters.orgCheckIn);
  const workStatusList = useAppSelector(state => state.common.workStatus);
  const entity = useAppSelector(state => state.checkInDraft.entity);
  const isLoading = useAppSelector(state => state.checkInDraft.loading);
  const isUpdating = useAppSelector(state => state.checkInDraft.updating);

  // init state
  const [isOpenModalFormKr, setIsOpenModalFormKr] = useState(false);
  const [isOpenModalFormObjective, setIsOpenModalFormObjective] = useState(false);
  const [isOpenDrawerComment, setOpenDrawerComment] = useState(false);
  const [isCheckLastCheckIn, setCheckLastCheckIn] = useState(false);
  const [isShowModalQuantitativeView, setShowModalQuantitativeView] = useState(false);
  const [isShowModalQualitativeView, setShowModalQualitativeView] = useState(false);

  // watch values form
  const workStatusValues = Form.useWatch('workStatus', form);
  const progressionPointValue = Form.useWatch('progressionPoint', form);
  const qualityPointValue = Form.useWatch('qualityPoint', form);

  useEffect(() => {
    if (krCheckIn && filters) {
      form.resetFields();
      innitData();
    }
  }, [krCheckIn]);

  useEffect(() => {
    if (entity) {
      if (entity?.workResult) {
        form.setFieldsValue({
          reportScheduleName: entity?.reportScheduleName,
          issue: entity?.workResult?.issue,
          note: entity?.workResult?.note,
          progressionPoint: displayPercent(entity?.workResult?.progressionPoint),
          qualityPoint: displayPercent(entity?.workResult?.qualityPoint),
          ratingNote: entity?.workResult?.ratingNote,
          solution: entity?.workResult?.solution,
          unFinishNote: entity?.workResult?.unFinishNote,
          workStatus: entity?.workResult?.workStatus,
          completeNote: entity?.workResult?.completeNote,
          completedOn: entity?.workResult?.completedOn !== null ? dayjs(entity?.workResult?.completedOn) : null,
          expectedCompleteOn: entity?.workResult?.expectedCompleteOn != null ? dayjs(entity?.workResult?.expectedCompleteOn) : null,
        })
      }
    }
  }, [entity]);

  useEffect(() => {
    if (qualityPointValue !== null || progressionPointValue !== null) {
      const { evaluationMethods } = entity?.workResult || {};
      const includesProgress = evaluationMethods?.includes(EvaluationTypes.Progress);
      const includesQuality = evaluationMethods?.includes(EvaluationTypes.Quality);

      if (includesProgress && !includesQuality) {
        form.setFieldValue('finalPoint', progressionPointValue);
      } else if (includesQuality && !includesProgress) {
        form.setFieldValue('finalPoint', qualityPointValue);
      } else {
        form.setFieldValue('finalPoint', (qualityPointValue + progressionPointValue) / 2);
      }
    }
  }, [qualityPointValue, progressionPointValue]);

  const innitData = () => {
    dispatch(getKrQualCheckInDraftYear(krCheckIn?.id));
  };

  const handleSubmit = values => {
    const payload = handleMapperDataBeforeSubmit(values)
    dispatch(checkIntKrQualDraftYear(payload)).then(response => {
      if (response && response.payload) {
        const result = (response.payload as any).data
        if (!result) return;
        dispatch(reset());
        form.resetFields();
        handleClose(true);
      }
    })
  };

  const handleNextKr = () => {
    form
      .validateFields()
      .then(values => {
        const payload = handleMapperDataBeforeSubmit(values)
        dispatch(checkIntKrQualDraftYear(payload))
          .then(response => {
            if (response && response.payload) {
              const result = (response.payload as any).data
              if (!result) return;
              dispatch(reset());
              setUpdateSuccess(true);
              setCheckLastCheckIn(false)
              form.resetFields();
              form.scrollToField('reportScheduleName');
              handleNextData();
            }
          })
          .catch(() => {
            return;
          });
      })
      .catch((err) => {
        return err;
      })
  };

  const handleMapperDataBeforeSubmit = (values) => {
    return {
      ...values,
      krId: krCheckIn?.id,
      completedOn: values.completedOn != null ? dayjs(values.completedOn).format(APP_LOCAL_DATE_FORMAT_PAYLOAD) : null,
      expectedCompleteOn: values.expectedCompleteOn != null ? dayjs(values.expectedCompleteOn).format(APP_LOCAL_DATE_FORMAT_PAYLOAD) : null,
      progressionPoint: values.progressionPoint && percent(values.progressionPoint),
      qualityPoint: values.qualityPoint && percent(values.qualityPoint),
    }
  }

  const handleEditKr = () => {
    setIsOpenModalFormKr(true);
  };

  const handleCloseModelKr = () => {
    setIsOpenModalFormKr(false);
  };

  const handleEditObjective = () => {
    setIsOpenModalFormObjective(true);
  };

  const handleCloseModelObjective = () => {
    setIsOpenModalFormObjective(false);
  };

  const handleAutoFillDataClosest = e => {
    setCheckLastCheckIn(true)
    form.setFieldValue('completeNote', e.target.checked ? entity?.keyResultNearest?.completeNote : null)
    form.setFieldValue('unFinishNote', e.target.checked ? entity?.keyResultNearest?.unFinishNote : null)
  }

  const handleShowDrawerComment = () => {
    setOpenDrawerComment(true);
  };

  const handleCloseDrawerComment = () => {
    setOpenDrawerComment(false);
  };

  const handleViewReportResult = (event) => {
    event.preventDefault();
    if (krCheckIn?.krType === KrTypes.Qualitative) {
      setShowModalQualitativeView(true);
    } else {
      setShowModalQuantitativeView(true);
    }
  };

  const handleCloseModalQuantitativeView = () => {
    setShowModalQuantitativeView(false);
  };
  const handleCloseModalQualitativeView = () => {
    setShowModalQualitativeView(false);
  };

  return (
    <Spin delay={500} spinning={isLoading}>
      <Form ref={formRef} size="large" form={form} className='fw-bold' onFinish={handleSubmit} layout="vertical">
        <Row>
          <Col md={20}>
            <Title className="fw-bold" level={2}>
              {`Báo cáo thực hiện KR ${filters?.accountableName ? filters.accountableName : ''}`}
            </Title>
          </Col>
          <Col md={1} />
          <Col md={3}><Button onClick={handleShowDrawerComment}>Ý kiến trao đổi</Button></Col>
        </Row>
        <Row>
          <Col md={5}>
            <Form.Item label="Kỳ kế hoạch">
              <Input className="w-100" disabled value={entity?.planPeriodName} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={5}>
            <Form.Item label="Kỳ báo cáo" name={'reportScheduleName'}>
              <Input size='large' readOnly />
            </Form.Item>
          </Col>
          {/* <Col md={1} />
          <Col>
            <div className="mt-4">
              <span className="cursor-pointer text-primary" onClick={() => handleEditKr()}>
                {`Xem chi tiết KR >`}
              </span>
              <br />
              <span className="cursor-pointer text-primary" onClick={() => handleEditObjective()}>
                {`Xem chi tiết mục tiêu >`}
              </span>
            </div>
          </Col> */}
        </Row>
        <Row>
          <Col md={11}>
            <Form.Item label={`Mục tiêu - ${entity?.objective?.code}`}>
              <TextArea rows={1} className="w-100" disabled value={entity?.objective?.purpose} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={3}>
            <Form.Item label="Lĩnh vực">
              <Input className="w-100" disabled value={entity?.objective?.fieldName} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={3}>
            <Form.Item label="Dự án">
              <Input className="w-100" disabled value={entity?.objective?.projectName} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={4}>
            <Form.Item label="Mảng công việc">
              <Input className="w-100" disabled value={entity?.objective?.workTypeName} size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col className="fw-bold h3" md={24}>
            {`Kết quả cần đạt được - ${entity?.krYear?.code}`}
          </Col>
        </Row>
        <Row>
          <Col md={11}>
            <Form.Item label={`KR năm ${entity?.planPeriodName}`}>
              <TextArea rows={1} className="w-100" disabled value={entity?.krYear?.purpose} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={5}>
            <Form.Item label="Ngày bắt đầu">
              <DatePicker
                className="w-100"
                disabled
                value={dayjs(entity?.krYear?.startDate)}
                placeholder="Chọn ngày bắt đầu"
                size="large"
                format={APP_LOCAL_DATE_FORMAT}
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={6}>
            <Form.Item label="Thời hạn hoàn thành">
              <DatePicker
                className="w-100"
                disabled
                value={dayjs(entity?.krYear?.dueDate)}
                placeholder="Chọn thời hạn hoàn thành"
                size="large"
                format={APP_LOCAL_DATE_FORMAT}
              />
            </Form.Item>
          </Col>
        </Row>

        <hr />
        <Row>
          <Col className="fw-bold h3" md={11}>
            Thực hiện {entity?.workResult?.name}
          </Col>
          <Col md={1} />
          <Col md={12}><span onClick={(e) => { handleViewReportResult(e) }} className='text-primary cursor-pointer fw-bold'>Xem kết quả thực hiện KR</span></Col>
        </Row>
        <Row>
          <Col>
            <Checkbox checked={isCheckLastCheckIn} onChange={handleAutoFillDataClosest}>Tự động điền nội dung kỳ check in gần nhất</Checkbox>
          </Col>
        </Row>
        <Row>
          <Col md={7}>
            <Form.Item label="Tình trạng"
              rules={[{ required: entity?.krYear?.purpose ? true : false, message: 'Hãy chọn tình trạng' }]}
              name="workStatus">
              <Select
                filterOption={filterOption}
                allowClear
                showSearch
                size="large"
                options={workStatusList}
                placeholder="Chọn tình trạng"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          {workStatusValues === StatusType.Completed ? (
            <Col md={7}>
              <Form.Item
                label={`Ngày hoàn thành`}
                rules={[
                  {
                    required: workStatusValues === StatusType.Completed,
                    message: `Hãy chọn ngày hoàn thành`,
                  },
                ]}
                name={`completedOn`}
              >
                <DatePicker
                  size="large"
                  format={APP_LOCAL_DATE_FORMAT} placeholder={`chọn ngày hoàn thành`} className="w-100" />
              </Form.Item>
            </Col>
          ) : undefined}
          {(workStatusValues === StatusType.NotCompleted || workStatusValues === StatusType.OnTrack) ? (
            <Col md={7}>
              <Form.Item
                label={`Ngày hoàn thành dự kiến`}
                rules={[
                  {
                    required: workStatusValues && (workStatusValues === StatusType.NotCompleted || workStatusValues === StatusType.OnTrack),
                    message: `Hãy chọn ngày hoàn thành dự kiến`,
                  },
                ]}
                name={`expectedCompleteOn`}
              >
                <DatePicker
                  size="large" format={APP_LOCAL_DATE_FORMAT} placeholder={`chọn ngày hoàn thành dự kiến`} className="w-100" />
              </Form.Item>
            </Col>
          ) : undefined}
        </Row>
        <Row>
          <Col md={11}>
            <Form.Item
              label="Đã hoàn thành"
              rules={[
                {
                  required: workStatusValues === StatusType.Completed,
                  message: `Hãy nhập nội dung đã hoàn thành`,
                },
              ]}
              name={'completeNote'}
            >
              <TextArea rows={6} placeholder="Nhập nội dung đã hoàn thành" size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={12}>
            <Form.Item
              label="Lý do không hoàn thành, Tạm dừng, Hủy"
              rules={[
                {
                  required: workStatusValues && workStatusValues !== StatusType.Completed,
                  message: `Hãy nhập lý do`,
                },
              ]}
              name={'unFinishNote'}
            >
              <TextArea rows={6} placeholder="Nhập lý do" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col className="fw-bold" md={24}>
            Đánh giá tỷ lệ hoàn thành
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <Form.Item
              label="Tiến độ"
              rules={[
                {
                  required:
                    entity?.krYear?.purpose && (workStatusValues === StatusType.Completed || workStatusValues === StatusType.NotCompleted) &&
                    entity?.workResult?.evaluationMethods?.includes(EvaluationTypes.Progress),
                  message: 'Vui lòng nhập',
                },
              ]}
              name={'progressionPoint'}
            >
              <InputNumber
                formatter={formatNumber}
                parser={parseNumber}
                className="w-100"
                disabled={!entity?.workResult?.evaluationMethods?.includes(EvaluationTypes.Progress)}
                placeholder="Nhập tiến độ"
                size="large"
                addonAfter="%"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={3}>
            <Form.Item
              label="Chất lượng"
              rules={[
                {
                  required:
                    entity?.krYear?.purpose && (workStatusValues === StatusType.Completed || workStatusValues === StatusType.NotCompleted) &&
                    entity?.workResult?.evaluationMethods?.includes(EvaluationTypes.Quality),
                  message: 'Vui lòng nhập',
                },
              ]}
              name={'qualityPoint'}
            >
              <InputNumber
                formatter={formatNumber}
                parser={parseNumber}
                className="w-100"
                disabled={!entity?.workResult?.evaluationMethods?.includes(EvaluationTypes.Quality)}
                placeholder="Nhập chất lượng"
                size="large"
                addonAfter="%"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={3}>
            <Form.Item label="Trung bình" initialValue={0} name={'finalPoint'}>
              <InputNumber min={0} formatter={formatNumber} parser={parseNumber} className="w-100" disabled size="large" addonAfter="%" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={12}>
            <Form.Item label="Ghi chú" name={'ratingNote'}>
              <Input className="w-100" placeholder="Nhập ghi chú" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col md={11}>
            <Form.Item label="Khó khăn & nguyên nhân" name={'issue'}>
              <TextArea rows={6} placeholder="Nhập khó khăn & nguyên nhân" size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={12}>
            <Form.Item label="Giải pháp và kiến nghị" name={'solution'}>
              <TextArea rows={6} placeholder="Nhập giải pháp và kiến nghị" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col md={24}>
            <Form.Item label="Tài liệu đính kèm về kết quả thực hiện (link)" name={'note'}>
              <TextArea placeholder="Nhập tài liệu đính kèm về kết quả thực hiện" size="large" />
            </Form.Item>
            <Form.Item valuePropName="checked" name={'sendingNotification'}>
              <Checkbox>Gửi thông báo đến người nhận</Checkbox>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <Row className="d-flex gap-3 mt-3" justify="end">
            <Col>
              <Button
                className="green"
                onClick={() => {
                  handleNextKr();
                }}
                disabled={isUpdating}
              >
                Lưu & Chuyển sang check in KR khác
              </Button>
            </Col>
            <Col>
              <Button className="primary" htmlType="submit" disabled={isUpdating}>
                Lưu
              </Button>
            </Col>
            <Col>
              <Button
                className="secondary"
                onClick={() => {
                  handleNextData();
                }}
              >
                Bỏ qua
              </Button>
            </Col>
            <Col>
              <Button
                className="secondary"
                onClick={() => {
                  handleClose();
                }}
              >
                Hủy
              </Button>
            </Col>
          </Row>
        </Form.Item>
        {isOpenModalFormObjective && (
          <ObjectiveUpdate isOpen={isOpenModalFormObjective} id={entity?.objective?.id} onClose={handleCloseModelObjective} />
        )}
        {isOpenModalFormKr && (
          <KRUpdate
            isOpen={isOpenModalFormKr}
            idKrBaseSelected={krCheckIn?.id}
            idObjective={entity?.objective?.id}
            planPeriodType={filters?.planPeriodType}
            krTypeRequest={krCheckIn?.krType}
            planPeriodIdSelected={filters?.planPeriodId}
            onClose={handleCloseModelKr}
          />
        )}
        {isShowModalQuantitativeView && (
          <ReportQuantitativeResultView
            isOpen={isShowModalQuantitativeView}
            idKrSelected={krCheckIn?.id}
            onClose={handleCloseModalQuantitativeView}
          />
        )}
        {isShowModalQualitativeView && (
          <ReportQualitativeResultView
            isOpen={isShowModalQualitativeView}
            idKrSelected={krCheckIn?.id}
            onClose={handleCloseModalQualitativeView}
          />
        )}
        {isOpenDrawerComment &&
          <CommentComponent topicId={krCheckIn?.id} handleDrawerComment={handleCloseDrawerComment} isOpenDrawerComment={isOpenDrawerComment} />
        }
      </Form>
    </Spin>
  );
};

export default ReportQualitativeResult;
